import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Tracks dismissed notification ids and the timestamp they were dismissed.
 *
 * Persisted to localStorage so dismissals survive reloads. Notification ids
 * are stable per source (e.g. promo alerts use `promo-{debtId}-{expiresAt}`)
 * which means a NEW promo on the same debt creates a fresh, undismissed alert.
 *
 * This store deliberately holds only UI state — the alert content itself is
 * always derived from the live data via hooks like `usePromoAlerts`.
 */
interface NotificationState {
  /** Map of notification id → ISO timestamp of when it was dismissed */
  dismissed: Record<string, string>
}

interface NotificationActions {
  dismiss: (id: string) => void
  undismiss: (id: string) => void
  clearAllDismissed: () => void
  isDismissed: (id: string) => boolean
}

export const useNotificationStore = create<NotificationState & NotificationActions>()(
  persist(
    (set, get) => ({
      dismissed: {},

      dismiss: (id) => {
        set((s) => ({
          dismissed: { ...s.dismissed, [id]: new Date().toISOString() },
        }))
      },

      undismiss: (id) => {
        set((s) => {
          const next = { ...s.dismissed }
          delete next[id]
          return { dismissed: next }
        })
      },

      clearAllDismissed: () => set({ dismissed: {} }),

      isDismissed: (id) => id in get().dismissed,
    }),
    {
      name: 'debtos:notifications:v1',
    }
  )
)
